export type Id = number | string

export type AgeRestrictedPlan = {
    min_age?: number | string | null
    max_age?: number | string | null
}

export type SelectedSubProduct = AgeRestrictedPlan & {
    id?: Id
    name?: string
    product_name?: string
    title?: string
    type?: string
    price?: number
    sku?: string
    subscriptionKey?: string
    active?: boolean
    iteration_type?: string
    iteration_charge?: string
    [key: string]: any
}

export type InsuranceAgentProduct = {
    id?: Id
    name?: string
    subscriptions?: SelectedSubProduct[]
}

export type SubscriptionProduct = {
    id?: Id
    name?: string
    product_name?: string
    type?: string
    price?: number
}

export type ProductDetails = {
    id?: Id
    name: string
    price: number
    sku?: string
    productType?: string
    subscriptionKey?: string | number
    iteration_type?: string
    iteration_charge?: string
}

export type FreePurchasePayloadOptions = {
    productDetails: {
        id: number | string
        price: number
        name: string
        sku: string
    }
    effectivePrice?: number
    email: string
    city: string
    address: string
    providedBy: number | string | null
    clientId: number | string
    subscriptionKey: string | number
}

export type ProductSelectionOption = {
    id?: Id
    name?: string
    product_name?: string
    type?: string
}

export type MonthlyProductsData = {
    productOptions: ProductSelectionOption[]
    productPlans: SelectedSubProduct[]
}

export type BundleProductsData = {
    uniqueBundles: ProductSelectionOption[]
    productPlans: SelectedSubProduct[]
}

export type ProductFlowData = {
    isBundlePlan: boolean
    finalProducts: ProductSelectionOption[]
    productPlans: SelectedSubProduct[]
}

export enum PURCHASE_PLANS {
    MONTHLY = 'monthly',
    BUNDLE = 'bundle'
}

// Normalize API price values so numeric strings like "0" behave the same as 0.
export function normalizeProductPrice(value: unknown): number {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!trimmedValue) {
            return 0
        }

        const parsedValue = Number(trimmedValue)
        return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    return 0
}

// Bundle products can wrap their age-limited entries inside a nested collection, so resolve that list before filtering.
function getBundleAgeEntries(subscription: SelectedSubProduct): AgeRestrictedPlan[] {
    const nestedEntries =
        subscription.items ||
        subscription.subscriptions ||
        subscription.plans ||
        subscription.products ||
        subscription.bundle_products

    if (Array.isArray(nestedEntries) && nestedEntries.length > 0) {
        return nestedEntries
    }

    return [subscription]
}

// Preferred ordering ensures the dropdown matches familiar product priority.
const PRODUCT_ORDER: Record<string, number> = {
    'Best Care': 1,
    'Best Care smile': 2,
    Critical: 3,
    'Simple Smile': 4,
    'MediWho Gold': 5,
    'Gold Plus': 6,
    'All-In': 7,
    'MediWho 360': 8,
    Assistant: 9,
    'מגן דוד כחול': 10,
    'Soul Care': 11,
    'Mediwho Hospital Plus': 12
}

const BLOCKED_PRODUCT_TEXT = 'הרשות הפלסטינית'

function includesBlockedProductText(value?: string | null): boolean {
    return !!value && value.includes(BLOCKED_PRODUCT_TEXT)
}

// Comparator that sorts two product options using the hard-coded priority map.
// Items missing from PRODUCT_ORDER fall to the end while preserving their relative order.
function sortByPreferredOrder(leftOption: ProductSelectionOption, rightOption: ProductSelectionOption): number {
    const leftOrder = leftOption.name ? PRODUCT_ORDER[leftOption.name] : undefined
    const rightOrder = rightOption.name ? PRODUCT_ORDER[rightOption.name] : undefined

    if (leftOrder === undefined && rightOrder === undefined) {
        return 0
    }
    if (leftOrder === undefined) {
        return 1
    }
    if (rightOrder === undefined) {
        return -1
    }
    return leftOrder - rightOrder
}

// Strip unavailable products and return options sorted by priority for the dropdown.
export function buildProductOptions(
    products: InsuranceAgentProduct[] = [],
    clientInfo: { birthday?: string } | null = null
): ProductSelectionOption[] {
    const filteredProducts = clientInfo
        ? filterProductsByClientAge(products as SelectedSubProduct[], clientInfo)
        : products

    const productOptions = filteredProducts
        .filter(product => {
            const productName = product.name || ''

            return (
                !productName.includes('Mediwho Fly') &&
                !productName.includes('Bundle') &&
                !includesBlockedProductText(productName)
            )
        })
        .map(product => ({
            id: product.id,
            name: product.name
        }))
        .filter(option => option.name !== 'תושבי חוץ')

    productOptions.sort(sortByPreferredOrder)
    return productOptions
}

// Normalize bundle names by removing parentheses and specific Hebrew words.
export function formatProductLabel(option: ProductSelectionOption): string {
    const sourceLabel = option.name || option.type || ''
    return sourceLabel
        .replace(/\(.+?\)/g, '')
        .replace(/[א-ת]*(בוגר|יחיד)[א-ת]*/g, '')
        .trim()
}

// Guard function that checks whether a plan fits inside the client's age brackets.
export function isWithinClientAgeRange(plan: AgeRestrictedPlan, clientAge: number | null): boolean {
    if (clientAge === null) {
        return true
    }

    if (plan.min_age !== undefined && plan.min_age !== null) {
        const min = Number(plan.min_age)
        // NaN comparisons are always false — treat non-numeric age limits as no restriction
        if (Number.isFinite(min) && clientAge < min) {
            return false
        }
    }

    if (plan.max_age !== undefined && plan.max_age !== null) {
        const max = Number(plan.max_age)
        if (Number.isFinite(max) && clientAge > max) {
            return false
        }
    }

    return true
}

// Build bundle groupings and receive only plans that match the selected bundle and client's age.
export function buildBundleProducts(
    subscriptions: SubscriptionProduct[] = [],
    selectedProduct: Id | null,
    clientInfo: { birthday?: string } | null
): BundleProductsData {
    const clientAge = calculateClientAge(clientInfo)
    const bundleSubscriptions = subscriptions.filter(subscription =>
        Boolean(subscription.type && subscription.type.toUpperCase().includes('BUNDLE'))
    )

    const seenBundleTypes: Record<string, boolean> = {}
    const uniqueBundles = bundleSubscriptions
        .filter(subscription => {
            const bundleType = subscription.type
            if (!bundleType || seenBundleTypes[bundleType]) {
                return false
            }
            seenBundleTypes[bundleType] = true
            return true
        })
        .map(subscription => ({
            id: subscription.id,
            type: subscription.type,
            name: subscription.name,
            product_name: subscription.product_name
        }))

    const selectedBundle =
        uniqueBundles.find(bundle => {
            if (bundle.id !== undefined && bundle.id !== null && selectedProduct !== null) {
                return String(bundle.id) === String(selectedProduct)
            }

            return bundle.name === selectedProduct || bundle.product_name === selectedProduct
        }) || null

    const filteredBundles = selectedBundle
        ? bundleSubscriptions.filter(subscription => subscription.type === selectedBundle.type)
        : []

    // Keep the original bundle objects intact and drop the whole bundle when any nested item fails the age rule.
    const productPlans = filteredBundles.filter(subscription =>
        getBundleAgeEntries(subscription).every(bundleItem => isWithinClientAgeRange(bundleItem, clientAge))
    )

    return { uniqueBundles, productPlans }
}

// Collect monthly product options with active subscriptions and age filtering.
export function buildMonthlyProducts(
    products: InsuranceAgentProduct[] = [],
    selectedProduct: Id | null,
    clientInfo: { birthday?: string } | null
): MonthlyProductsData {
    const productOptions = buildProductOptions(products, clientInfo)
    const selectedProductOption =
        productOptions.find(option => {
            if (option.id !== undefined && option.id !== null && selectedProduct !== null) {
                return String(option.id) === String(selectedProduct)
            }

            return option.name === selectedProduct
        }) || null

    const productSubscriptions = selectedProductOption
        ? products.find(product => {
              if (
                  selectedProductOption.id !== undefined &&
                  selectedProductOption.id !== null &&
                  product.id !== undefined &&
                  product.id !== null
              ) {
                  return String(product.id) === String(selectedProductOption.id)
              }

              return product.name === selectedProductOption.name
          }) || null
        : null

    const baseProductPlans =
        productSubscriptions && productSubscriptions.subscriptions
            ? productSubscriptions.subscriptions
                  .map(subscription => ({
                      ...subscription,
                      product_id: subscription.product_id || (subscription.pivot && subscription.pivot.product_id)
                  }))
                  .filter(subscription => {
                      return !!subscription.active === true
                  })
                  .filter(subscription => {
                      const blocked =
                          includesBlockedProductText(subscription.name) ||
                          includesBlockedProductText(subscription.product_name)
                      return !blocked
                  })
                  .filter(subscription => {
                      if (!selectedProductOption || !selectedProductOption.id) {
                          return true
                      }
                      const productId = subscription.product_id
                      return productId && String(productId) === String(selectedProductOption.id)
                  })
            : []

    const productPlans = filterProductsByClientAge(baseProductPlans, clientInfo)

    return { productOptions, productPlans }
}

// Check if client is a non-resident based on passport and identity fields
export function isNonResident(clientInfo: { identity?: string; passport?: string } | null): boolean {
    if (!clientInfo) {
        return false
    }
    return !!clientInfo.passport && !clientInfo.identity
}

// Build product flow data for non-resident clients
export function buildNonResidentProductFlowData(
    selectedProduct: Id | null,
    clientInfo: { birthday?: string } | null,
    products: InsuranceAgentProduct[]
): ProductFlowData {
    const NON_RESIDENT_PRODUCT_NAME = 'תושבי חוץ'
    const nonResidentProduct = products.find(p => !!p.name && p.name.includes(NON_RESIDENT_PRODUCT_NAME))

    const finalProducts: ProductSelectionOption[] = nonResidentProduct
        ? [{ id: nonResidentProduct.id, name: nonResidentProduct.name }]
        : []

    const baseProductPlans =
        selectedProduct && nonResidentProduct && nonResidentProduct.subscriptions
            ? nonResidentProduct.subscriptions
                  .filter(s => !!s.active === true)
                  .filter(p => !includesBlockedProductText(p.name) && !includesBlockedProductText(p.product_name))
                  .filter(
                      p =>
                          (p.name && p.name.includes(NON_RESIDENT_PRODUCT_NAME)) ||
                          (p.product_name && p.product_name.includes(NON_RESIDENT_PRODUCT_NAME))
                  )
            : []

    const productPlans = filterProductsByClientAge(baseProductPlans as SelectedSubProduct[], clientInfo)

    return { isBundlePlan: false, finalProducts, productPlans }
}

// Decide which collection (bundle vs monthly) drives the dropdown and plan lists.
export function buildProductFlowData(
    selectedPlan: PURCHASE_PLANS | null,
    selectedProduct: Id | null,
    clientInfo: { birthday?: string } | null,
    products: InsuranceAgentProduct[],
    subscriptions: SubscriptionProduct[]
): ProductFlowData {
    const monthlyProductsData = buildMonthlyProducts(products, selectedProduct, clientInfo)
    const bundleProductsData = buildBundleProducts(subscriptions, selectedProduct, clientInfo)
    const isBundlePlan = selectedPlan === PURCHASE_PLANS.BUNDLE

    return {
        isBundlePlan,
        finalProducts: isBundlePlan ? bundleProductsData.uniqueBundles : monthlyProductsData.productOptions,
        productPlans: isBundlePlan ? bundleProductsData.productPlans : monthlyProductsData.productPlans
    }
}

// Generate stable keys for sub-product options so Select can track the choices.
export function buildSubProductOptionValue(option: SelectedSubProduct, index: number): string {
    if (option.id !== undefined && option.id !== null) {
        return String(option.id)
    }

    if (option.product_name) {
        return option.product_name
    }

    if (option.name) {
        return option.name
    }

    if (option.sku) {
        return option.sku
    }

    if (option.type) {
        return option.type
    }

    return `option-${index}`
}

// Map option values back to the original sub-product objects for later selection.
export function buildSubProductLookup(products: SelectedSubProduct[] = []): Record<string, SelectedSubProduct> {
    const lookup: Record<string, SelectedSubProduct> = {}

    products.forEach((product, index) => {
        const optionValue = buildSubProductOptionValue(product, index)
        if (optionValue in lookup) {
            console.warn(
                `[buildSubProductLookup] Key collision on "${optionValue}" at index ${index} — earlier entry overwritten`
            )
        }
        lookup[optionValue] = product
    })

    return lookup
}

// Build the display label for each sub-product, appending the price when available.
export function buildSubProductOptionLabel(subProduct: SelectedSubProduct, optionValue: string): string {
    const baseSource =
        subProduct.name ||
        subProduct.product_name ||
        subProduct.name ||
        subProduct.title ||
        subProduct.id ||
        optionValue
    const baseLabel = baseSource !== undefined && baseSource !== null ? String(baseSource) : optionValue

    if (subProduct.price !== undefined && subProduct.price !== null) {
        return `${baseLabel} - ${subProduct.price} ₪`
    }
    return baseLabel
}

// Build the payload that the Tranzila modal expects, preferring the selected sub-product when available.
export function buildTranzilaProductDetails(
    productDetails: ProductDetails,
    selected: SelectedSubProduct | null
): ProductDetails {
    if (!selected) {
        return {
            ...productDetails,
            price: normalizeProductPrice(productDetails.price),
            productType: 'monthly'
        }
    }

    const selectedPrice = normalizeProductPrice(selected.price)
    const fallbackPrice = normalizeProductPrice(productDetails.price)
    return {
        id: selected.id !== undefined && selected.id !== null ? selected.id : productDetails.id,
        name: selected.name || selected.product_name || productDetails.name,
        sku: selected.sku || productDetails.sku || '',
        price: selected.price !== undefined && selected.price !== null ? selectedPrice : fallbackPrice,
        productType: 'monthly',
        subscriptionKey: selected.subscriptionKey || productDetails.subscriptionKey || '',
        iteration_type: selected.iteration_type || productDetails.iteration_type,
        iteration_charge: selected.iteration_charge || productDetails.iteration_charge
    }
}

// Build the free-purchase request payload only after the awaiting-payment row exists.
export function buildFreePurchasePayload(options: FreePurchasePayloadOptions): Record<string, any> {
    const normalizedPrice =
        options.effectivePrice !== undefined
            ? normalizeProductPrice(options.effectivePrice)
            : normalizeProductPrice(options.productDetails.price)

    return {
        product_id: options.productDetails.id,
        product_price: normalizedPrice,
        product_name: options.productDetails.name,
        email: options.email,
        sku: options.productDetails.sku,
        city: options.city || '',
        address: options.address || '',
        provided_by: options.providedBy,
        user_id: options.clientId,
        subscriptionKey: options.subscriptionKey,
        isFly: false,
        price: normalizedPrice,
        isRdg: false,
        isPrepayment: options.isPrepayment || false,
        prepaymentMonths: options.prepaymentMonths
    }
}

// Helper to keep the client age calculation centralized for product filtering.
export function calculateClientAge(clientInfo: { birthday?: string } | null): number | null {
    if (!clientInfo || !clientInfo.birthday) {
        return null
    }

    const birthDate = new Date(clientInfo.birthday)
    if (Number.isNaN(birthDate.getTime())) {
        return null
    }

    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const birthdayHasPassed =
        today.getMonth() > birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate())

    if (!birthdayHasPassed) {
        age -= 1
    }

    return age
}

// Filter products by client age constraints
export function filterProductsByClientAge(
    products: SelectedSubProduct[],
    clientInfo: { birthday?: string } | null
): SelectedSubProduct[] {
    const clientAge = calculateClientAge(clientInfo)

    return products.filter(product => isWithinClientAgeRange(product, clientAge))
}

// Check for duplicate payment and handle the result with modal feedback.
// Calls onSuccess if check passes, otherwise shows warning modal with API message and reloads page.
export async function handleDuplicatePaymentCheck(
    auth: any,
    userId: number | string,
    subscriptionId: number | string,
    onSuccess: () => void
): Promise<void> {
    let response
    try {
        response = await auth.checkDuplicatePayment({
            user_id: userId,
            subscription_id: subscriptionId
        })
    } catch (err) {
        console.error('[handleDuplicatePaymentCheck] Duplicate payment check failed', err)
        const Modal = (await import('antd')).Modal
        Modal.error({ title: 'שגיאה', content: 'אירעה שגיאה. נסה שנית.' })
        return
    }

    if (response.status === 200 && response.data === 'ללקוח יש כבר מינוי פעיל') {
        const Modal = (await import('antd')).Modal
        Modal.warning({
            title: 'שגיאה',
            content: response.data,
            okText: 'חזור',
            maskClosable: true,
            onOk: () => window.location.reload(),
            onCancel: () => window.location.reload()
        })
        return
    }

    if (response.data) {
        const Modal = (await import('antd')).Modal
        Modal.warning({
            title: 'שגיאה',
            content: response.data,
            okText: 'חזור'
        })
        return
    }

    onSuccess()
}
